// access.js - Centralized authentication and authorization utilities
import BASE_URL from "../config";
const API_BASE_URL = BASE_URL;

class AccessManager {
  constructor() {
    this.baseURL = API_BASE_URL;
    // Use localStorage for persistence across page refreshes
    this.tokenCheckInterval = null;
    this.onLogout = null;
    this.refreshAttempts = 0;
    this.maxRefreshAttempts = 2;
    this.refreshInProgress = false;
    this.lastActivity = Date.now();
    
    // Extended activity timeout to 2 hours
    this.activityTimeout = 7200000; // 2 hours in milliseconds
    
    // Track if user explicitly wants to stay logged in
    this.stayLoggedIn = true;
    
    // Inactivity warning settings
    this.inactivityWarningTime = 1800000; // 30 minutes before timeout
    this.warningShown = false;
    
    // Initialize tokens from localStorage on startup
    this.initializeFromStorage();
    
    // Set up user activity tracking
    this.setupActivityTracking();
  }

  // Initialize tokens from localStorage
  initializeFromStorage() {
    try {
      const accessToken = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');
      const userDataStr = localStorage.getItem('user_data');
      
      if (accessToken && refreshToken) {
        // Check if token is still valid
        const tokenInfo = this.getTokenExpiryInfo(accessToken);
        if (tokenInfo && !tokenInfo.isExpired) {
          // Token is valid, restore session
          console.log('Restored session from localStorage');
          this.setupTokenExpirationCheck();
        } else {
          // Token expired, clear storage
          console.log('Stored token expired, clearing storage');
          this.clearStorage();
        }
      }
    } catch (error) {
      console.error('Error initializing from storage:', error);
      this.clearStorage();
    }
  }

  // Clear all storage
  clearStorage() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('last_activity');
    localStorage.removeItem('stay_logged_in');
  }

  // More comprehensive activity tracking
  setupActivityTracking() {
    const updateActivity = () => {
      this.lastActivity = Date.now();
      localStorage.setItem('last_activity', this.lastActivity.toString());
      this.warningShown = false;
    };

    // Track various user interactions
    const events = [
      'mousedown', 'mousemove', 'mouseup', 'click', 'dblclick',
      'keypress', 'keydown', 'keyup',
      'scroll', 'wheel',
      'touchstart', 'touchmove', 'touchend',
      'focus', 'blur',
      'resize',
      'visibilitychange'
    ];
    
    events.forEach(event => {
      document.addEventListener(event, updateActivity, true);
    });

    // Store event listeners for cleanup
    this.activityCleanup = () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity, true);
      });
    };
  }

  // More intelligent activity checking
  isUserActive() {
    const timeSinceActivity = Date.now() - this.lastActivity;
    return timeSinceActivity < this.activityTimeout;
  }

  // Check if user should receive inactivity warning
  shouldShowInactivityWarning() {
    const timeSinceActivity = Date.now() - this.lastActivity;
    return timeSinceActivity > this.inactivityWarningTime && 
           timeSinceActivity < this.activityTimeout && 
           !this.warningShown && 
           this.isAuthenticated();
  }

  // Show inactivity warning
  showInactivityWarning() {
    if (this.shouldShowInactivityWarning()) {
      this.warningShown = true;
      const remainingTime = Math.ceil((this.activityTimeout - (Date.now() - this.lastActivity)) / 60000);
      
      console.warn(`You will be logged out in ${remainingTime} minutes due to inactivity.`);
      
      if (this.onInactivityWarning) {
        this.onInactivityWarning(remainingTime);
      }
      
      return true;
    }
    return false;
  }

  // Set inactivity warning callback
  setInactivityWarningCallback(callback) {
    this.onInactivityWarning = callback;
  }

  // Extended activity timeout options
  setActivityTimeout(timeout) {
    this.activityTimeout = timeout;
    this.inactivityWarningTime = Math.max(timeout - 1800000, timeout * 0.75);
  }

  // Get authentication headers
  getAuthHeaders() {
    const token = localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  }

  // Less aggressive request handling
  async request(endpoint, options = {}) {
    const url = endpoint.startsWith('http') ? endpoint : `${this.baseURL}${endpoint}`;
    const config = {
      headers: this.getAuthHeaders(),
      ...options,
      headers: {
        ...this.getAuthHeaders(),
        ...(options.headers || {})
      }
    };

    try {
      const response = await fetch(url, config);
      
      // If token is expired, try to refresh
      if (response.status === 401 && localStorage.getItem('refresh_token')) {
        console.log('Received 401, attempting token refresh...');
        
        if (this.isUserActive() || this.stayLoggedIn) {
          const refreshed = await this.refreshTokenWithRetry();
          if (refreshed) {
            // Retry the original request with new token
            config.headers = {
              ...this.getAuthHeaders(),
              ...(options.headers || {})
            };
            const retryResponse = await fetch(url, config);
            if (retryResponse.ok) {
              return await retryResponse.json();
            } else if (retryResponse.status === 401) {
              console.log('Still unauthorized after refresh');
              this.logout();
              throw new Error('Authentication failed');
            } else {
              throw new Error(`HTTP error! status: ${retryResponse.status}`);
            }
          } else {
            console.log('Token refresh failed, logging out');
            this.logout();
            throw new Error('Authentication failed');
          }
        } else {
          console.log('User inactive and not staying logged in, logging out');
          this.logout();
          throw new Error('Session expired due to inactivity');
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData?.error || errorData?.message || `HTTP error! status: ${response.status}`);
        error.response = {
          status: response.status,
          data: errorData
        };
        throw error;
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Set authentication tokens
  setTokens(accessToken, refreshToken, userData) {
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('user_data', JSON.stringify(userData));
    localStorage.setItem('last_activity', Date.now().toString());
    localStorage.setItem('stay_logged_in', this.stayLoggedIn.toString());
    
    if (accessToken) {
      this.setupTokenExpirationCheck();
    }
  }

  // Get current tokens
  getTokens() {
    return {
      access_token: localStorage.getItem('access_token'),
      refresh_token: localStorage.getItem('refresh_token'),
      user_data: this.getCurrentUser()
    };
  }

  // Get access token
  getAccessToken() {
    return localStorage.getItem('access_token');
  }

  // More reliable refresh with better error handling
  async refreshTokenWithRetry() {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return false;

    for (let attempt = 1; attempt <= this.maxRefreshAttempts; attempt++) {
      try {
        console.log(`Token refresh attempt ${attempt}/${this.maxRefreshAttempts}`);
        
        const response = await fetch(`${this.baseURL}/auth/token/refresh/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ refresh: refreshToken })
        });

        if (response.ok) {
          const data = await response.json();
          localStorage.setItem('access_token', data.access);
          
          // Keep the same refresh token since rotation is disabled
          if (data.refresh) {
            localStorage.setItem('refresh_token', data.refresh);
          }
          
          console.log('Token refresh successful');
          return true;
        } else if (response.status === 401) {
          console.log('Refresh token is invalid/expired');
          return false;
        } else {
          console.warn(`Token refresh attempt ${attempt} failed with status ${response.status}`);
          if (attempt < this.maxRefreshAttempts) {
            await new Promise(resolve => setTimeout(resolve, 2000 * attempt));
          }
        }
      } catch (error) {
        console.error(`Token refresh attempt ${attempt} failed:`, error);
        if (attempt < this.maxRefreshAttempts) {
          await new Promise(resolve => setTimeout(resolve, 2000 * attempt));
        }
      }
    }
    
    return false;
  }

  // Legacy refresh method for backward compatibility
  async refreshToken() {
    return await this.refreshTokenWithRetry();
  }

  // Less aggressive token refresh handling
  async handleTokenRefresh() {
    if (this.refreshInProgress) return;
    
    this.refreshInProgress = true;
    
    try {
      const refreshed = await this.refreshTokenWithRetry();
      if (refreshed) {
        this.refreshAttempts = 0;
        console.log('Token refreshed successfully');
      } else {
        console.warn('Token refresh failed after all attempts');
      }
    } finally {
      this.refreshInProgress = false;
    }
  }

  // Handle completely expired token
  async handleTokenExpiration() {
    if (this.refreshInProgress) return;
    
    if (!this.stayLoggedIn && !this.isUserActive()) {
      console.log('Token expired, user inactive, logging out');
      this.logout();
      return;
    }
    
    this.refreshInProgress = true;
    
    try {
      const refreshed = await this.refreshTokenWithRetry();
      if (!refreshed) {
        console.log('Token expired and refresh failed, logging out');
        this.logout();
      }
    } finally {
      this.refreshInProgress = false;
    }
  }

  // Much less aggressive token checking with inactivity warnings
  setupTokenExpirationCheck() {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
    }

    // Check every 5 minutes instead of 30 seconds
    this.tokenCheckInterval = setInterval(() => {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      try {
        const tokenInfo = this.getTokenExpiryInfo(token);
        if (!tokenInfo) return;
        
        const timeUntilExpiry = tokenInfo.timeUntilExpiry;
        
        // Show inactivity warning if needed
        this.showInactivityWarning();
        
        // If token is already expired
        if (timeUntilExpiry <= 0) {
          console.log('Token expired, attempting refresh...');
          this.handleTokenExpiration();
        }
        // If token expires within 1 hour AND user has been active recently
        else if (timeUntilExpiry < 3600 && !this.refreshInProgress) {
          if (this.isUserActive() || this.stayLoggedIn) {
            console.log(`Token expires in ${Math.round(timeUntilExpiry)} seconds, refreshing...`);
            this.handleTokenRefresh();
          } else {
            console.log(`Token expires in ${Math.round(timeUntilExpiry)} seconds but user inactive, will wait`);
          }
        }
      } catch (error) {
        console.error('Error checking token expiration:', error);
      }
    }, 300000); // Check every 5 minutes
  }

  // Set stay logged in preference
  setStayLoggedIn(stayLoggedIn) {
    this.stayLoggedIn = stayLoggedIn;
    localStorage.setItem('stay_logged_in', stayLoggedIn.toString());
  }

  // Manual refresh token (for user-initiated refresh)
  async forceRefreshToken() {
    this.refreshInProgress = false;
    return await this.handleTokenRefresh();
  }

  // More lenient expiry checking
  isTokenNearExpiry(minutesThreshold = 60) {
    const token = localStorage.getItem('access_token');
    if (!token) return false;

    try {
      const tokenInfo = this.getTokenExpiryInfo(token);
      return tokenInfo && tokenInfo.timeUntilExpiry < (minutesThreshold * 60);
    } catch (error) {
      return false;
    }
  }

  // Get token expiry info (for debugging)
  getTokenExpiryInfo(token = null) {
    const accessToken = token || localStorage.getItem('access_token');
    if (!accessToken) return null;

    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      const currentTime = Date.now() / 1000;
      const timeUntilExpiry = payload.exp - currentTime;
      
      return {
        expiresAt: new Date(payload.exp * 1000),
        currentTime: new Date(currentTime * 1000),
        timeUntilExpiry: Math.round(timeUntilExpiry),
        timeUntilExpiryMinutes: Math.round(timeUntilExpiry / 60),
        isExpired: timeUntilExpiry <= 0,
        isNearExpiry: timeUntilExpiry < 3600 // 1 hour
      };
    } catch (error) {
      return null;
    }
  }

  // Better logout handling
  async logout() {
    const refreshToken = localStorage.getItem('refresh_token');
    
    if (refreshToken) {
      try {
        await fetch(`${this.baseURL}/auth/logout/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ refresh: refreshToken })
        });
      } catch (error) {
        console.error('Logout API call failed:', error);
      }
    }

    // Clear tokens from localStorage
    this.clearStorage();
    
    // Clear any running intervals
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
      this.tokenCheckInterval = null;
    }
    
    // Clean up activity tracking
    if (this.activityCleanup) {
      this.activityCleanup();
      this.activityCleanup = null;
    }
    
    // Reset warning state
    this.warningShown = false;
    
    // Trigger logout callback if set
    if (this.onLogout) {
      this.onLogout();
    }
  }

  // Enhanced activity info
  getActivityInfo() {
    const lastActivityStr = localStorage.getItem('last_activity');
    const lastActivityTime = lastActivityStr ? parseInt(lastActivityStr) : this.lastActivity;
    const timeSinceActivity = Date.now() - lastActivityTime;
    const timeUntilWarning = Math.max(0, this.inactivityWarningTime - timeSinceActivity);
    const timeUntilLogout = Math.max(0, this.activityTimeout - timeSinceActivity);
    
    return {
      lastActivity: new Date(lastActivityTime),
      timeSinceActivity: Math.round(timeSinceActivity / 1000),
      timeSinceActivityMinutes: Math.round(timeSinceActivity / 60000),
      isActive: this.isUserActive(),
      activityTimeout: this.activityTimeout / 1000,
      activityTimeoutMinutes: this.activityTimeout / 60000,
      timeUntilWarning: Math.round(timeUntilWarning / 60000),
      timeUntilLogout: Math.round(timeUntilLogout / 60000),
      stayLoggedIn: this.stayLoggedIn,
      warningShown: this.warningShown
    };
  }

  // Set logout callback for context updates
  setLogoutCallback(callback) {
    this.onLogout = callback;
  }

  // Get current user data
  getCurrentUser() {
    const userDataStr = localStorage.getItem('user_data');
    if (!userDataStr) return null;
    
    try {
      return JSON.parse(userDataStr);
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = localStorage.getItem('access_token');
    if (!token) return false;
    
    // Check if token is expired
    const tokenInfo = this.getTokenExpiryInfo(token);
    return tokenInfo && !tokenInfo.isExpired;
  }

  // Check if user has specific role
  hasRole(role) {
    const user = this.getCurrentUser();
    return user && user.role === role;
  }

  // Check if user has any of the specified roles
  hasAnyRole(roles) {
    const user = this.getCurrentUser();
    return user && roles.includes(user.role);
  }

  // Role-based permission helpers
  isAdmin() {
    return this.hasRole('admin');
  }

  isUser() {
    return this.hasRole('user');
  }

  isSecretary() {
    return this.hasRole('secretary');
  }

  // Permission checking methods
  canManageUsers() {
    return this.hasRole('admin');
  }

  canManageBranches() {
    return this.hasAnyRole(['admin', 'secretary']);
  }

  canManageCompanyVariables() {
    return this.hasRole('admin');
  }

  canManageAPIConfigurations() {
    return this.hasRole('admin');
  }

  canEditUser(userRole) {
    const currentUser = this.getCurrentUser();
    if (!currentUser) return false;
    
    if (currentUser.role === 'admin') {
      return userRole !== 'admin' || currentUser.role === 'admin';
    }
    
    return false;
  }

  canDeleteUser(userRole) {
    const currentUser = this.getCurrentUser();
    if (!currentUser) return false;
    
    return currentUser.role === 'admin' && userRole !== 'admin';
  }

  // Get user branch access
  getUserBranch() {
    const user = this.getCurrentUser();
    return user ? user.branch : null;
  }

  getUserBranchId() {
    const user = this.getCurrentUser();
    return user ? user.branch_id : null;
  }

  // Check if user has access to specific branch
  hasAccessToBranch(branchId) {
    const user = this.getCurrentUser();
    if (!user) return false;
    
    if (user.role === 'admin') return true;
    
    return user.branch_id === branchId || user.branch_id === null;
  }

  // Helper method to ensure array response
  ensureArray(response) {
    if (Array.isArray(response)) return response;
    if (response && Array.isArray(response.results)) return response.results;
    if (response && response.data && Array.isArray(response.data)) return response.data;
    return [];
  }

  // Validation helpers
  validateUserData(userData, isEdit = false) {
    const errors = {};
    
    if (!userData.email) {
      errors.email = 'Email is required';
    }
    
    if (!userData.username) {
      errors.username = 'Username is required';
    }
    
    if (!isEdit && !userData.password) {
      errors.password = 'Password is required';
    }
    
    if (userData.password && userData.password !== userData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    if (!userData.role) {
      errors.role = 'Role is required';
    }
    
    if (!userData.firstName && !userData.lastName) {
      errors.name = 'At least first name or last name is required';
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  // Get base API URL
  getBaseURL() {
    return this.baseURL;
  }

  // Set base API URL
  setBaseURL(url) {
    this.baseURL = url;
  }
}

// Create and export singleton instance
const accessManager = new AccessManager();

// Export both the class and the singleton instance
export { AccessManager };
export default accessManager;